// Общее для всех вкладок админки: запрос и типы. Вход тут не по initData, а по
// сессии-куке — админка открывается в обычном браузере, а не в мини-аппе.

export async function call<TResult>(path: string, body?: unknown): Promise<TResult> {
  const response = await fetch(path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  const payload = (await response.json()) as Record<string, unknown>
  if (!response.ok) throw new Error(typeof payload.error === 'string' ? payload.error : 'Ошибка')
  return payload as TResult
}

export interface AdminPerson {
  id: string
  telegramId: string
  firstName: string
  lastName: string
  username: string
  createdAt: string
  lastSeenAt: string
  forgottenAt: string | null
  hasProfile: boolean
  hasPortrait: boolean
  fullAtlas: boolean
  subscribedUntil: string | null
  paidPurchases: number
  paidRub: number
  visitsToday: number
  visitsWeek: number
  visitsTotal: number
  adminNote: string
}

export interface AdminEvent {
  at: string
  kind: string
  title: string
  note: string
}

export interface AdminPersonCard {
  person: AdminPerson
  profile: {
    displayName: string
    birthDate: string
    birthTime: string | null
    timeAccuracy: string
    placeLabel: string
    gender: string
    interest: string
  } | null
  partners: number
  couples: { id: string; displayName: string; hasReading: boolean; createdAt: string }[]
  counts: { dayCards: number; oracleQuestions: number; chapters: number; exports: number }
  purchases: { productKey: string; status: string; amountRub: number; manual: boolean; createdAt: string }[]
  visitDays: { day: string; count: number }[]
  events: AdminEvent[]
}

const PRODUCT_TITLES: Record<string, string> = {
  atlas_full: 'Полный Атлас',
  atlas_sub_30d: 'Подписка',
  atlas_couple_report: 'Отчёт пары',
}

export function productTitle(key: string): string {
  return PRODUCT_TITLES[key] ?? key
}

/** Когда человек был в последний раз: «сейчас», «3 ч назад», «12.08». */
export function relativeTime(iso: string): string {
  const at = new Date(iso).getTime()
  if (Number.isNaN(at)) return '—'
  const minutes = Math.round((Date.now() - at) / 60_000)
  if (minutes < 2) return 'сейчас'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  const days = Math.round(hours / 24)
  if (days < 8) return `${days} дн назад`
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

/**
 * Ровно 14 столбиков, даже если человек заходил один раз: иначе единственный
 * день растягивается во всю ширину и выглядит как бурная жизнь.
 */
export function visitGrid(days: { day: string; count: number }[]): { day: string; count: number }[] {
  const known = new Map(days.map((item) => [item.day, item.count]))
  const grid: { day: string; count: number }[] = []
  const cursor = new Date()
  cursor.setDate(cursor.getDate() - 13)
  for (let index = 0; index < 14; index += 1) {
    const day = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`
    grid.push({ day, count: known.get(day) ?? 0 })
    cursor.setDate(cursor.getDate() + 1)
  }
  return grid
}

export function shortDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU')
}

export function shortDateTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
